import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  SportsEsports, 
  CalendarToday, 
  LocationOn
} from '@mui/icons-material';
import MKBox from '../components/MKBox';
import MKTypography from '../components/MKTypography';
import MKButton from '../components/MKButton';
import { PRIMARY_PINK, PRIMARY_PINK_DARK } from '../constants';
import API_BASE_URL from '../apiConfig';
import SEOHead from '../components/SEO/SEOHead';
import StructuredData from '../components/SEO/StructuredData';

function MixCupPage() {
  const theme = useTheme();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // TODO: Replace these with actual event UUIDs
  const eventIds = [
    'dca8cfaa-a45b-423f-a201-952aed978dbd', // Replace with actual UUID
    '71b2da95-7b58-48a5-aa4c-b0fa2fe96ab3', // Replace with actual UUID  
    '994e7c82-4348-4f51-a2bf-e521f7bb20a7', // Replace with actual UUID
    '0aa8070c-b758-419e-ad0a-9afcf32ac47f'  // Replace with actual UUID
  ];

  const tournamentColors = [
    "#ff6b35", // Orange
    "#4ecdc4", // Teal
    "#45b7d1", // Blue
    "#f9ca24"  // Yellow
  ];

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      setError('');

      const tournamentPromises = eventIds.map(async (eventId, index) => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/events/${eventId}`, {
            credentials: 'include'
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch event ${eventId}`);
          }

          const eventData = await response.json();
          
          // Format the event data for display
          return {
            id: eventData.id,
            name: eventData.title,
            date: new Date(eventData.start_time).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }),
            time: new Date(eventData.start_time).toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            }),
            location: eventData.location || 'TBD',
            color: tournamentColors[index],
            eventData: eventData // Store full event data for navigation
          };
        } catch (error) {
          console.error(`Error fetching event ${eventId}:`, error);
          // Return a placeholder for failed events
          return {
            id: eventId,
            name: `Event ${index + 1}`,
            date: 'TBD',
            time: 'TBD',
            location: 'TBD',
            color: tournamentColors[index],
            error: true
          };
        }
      });

      const tournamentData = await Promise.all(tournamentPromises);
      setTournaments(tournamentData);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
      setError('Failed to load tournament data');
    } finally {
      setLoading(false);
    }
  };



  const handleTournamentClick = (tournament) => {
    if (tournament.error) {
      console.log('Cannot navigate to failed event:', tournament.id);
      return;
    }
    
    // Navigate to the event page
    window.open(`/events/${tournament.id}`, '_blank');
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: "100vh", 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: "100vh", py: 8 }}>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ textAlign: 'center' }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", pt: 0, pb: 0 }}>
      <SEOHead 
        title="Mix Cup Vol. 1 Tournament - San Diego Gaming"
        description="Join the Mix Cup Vol. 1 tournament featuring Street Fighter 6, Tekken 8, Guilty Gear Strive, and Mortal Kombat 1. Free to enter with $100 first place prize!"
        keywords="mix cup tournament, san diego gaming tournament, street fighter 6, tekken 8, guilty gear strive, mortal kombat 1, esports tournament, gaming competition"
        url="https://sandiegogaming.com/mixcup"
      />
      <StructuredData 
        type="event" 
        data={{
          name: "Mix Cup Vol. 1 Tournament",
          description: "Free to enter! $100 First Place Prize, merch giveaways to group winners! Players will be put into random groups of 4. The winner of the group will move onto the next round.",
          startDate: "2025-09-21T21:00:00-07:00",
          endDate: "2025-09-21T23:00:00-07:00",
          location: "Novo Mission Valley",
          price: "0"
        }}
      />
      <Container maxWidth="lg">
        {/* Tournament Flyer */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Card
            sx={{
              maxWidth: 800,
              mx: "auto",
              pb: 0,
              overflow: "hidden",
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark' 
                ? '0 8px 32px rgba(0,0,0,0.3)' 
                : '0 8px 32px rgba(0,0,0,0.1)'
            }}
          >
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <img
                src="/flyers/mix cup banner.png"
                alt="Mix Cup Vol. 1 Tournament Flyer - Tournament information and details"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }}
                loading="lazy"
              />
            </CardContent>
          </Card>
        </Box>

        {/* Tournament Information */}
        <Box sx={{ mb: 8 }}>
          <MKTypography
            variant="h3"
            fontWeight="bold"
            mb={4}
            sx={{ 
              textAlign: "center",
              color: theme.palette.mode === 'dark' ? 'white !important' : '#333333 !important'
            }}
          >
            Tournament Schedule
          </MKTypography>
          
          <Grid container spacing={4}>
            {tournaments.map((tournament) => (
              <Grid item xs={12} sm={6} md={3} key={tournament.id}>
                <Card
                  sx={{
                    height: '100%',
                    background: theme.palette.mode === 'dark' 
                      ? 'linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)'
                      : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    border: `2px solid ${tournament.color}`,
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 25px ${tournament.color}40`
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Game Icon */}
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <SportsEsports 
                        sx={{ 
                          fontSize: 40, 
                          color: tournament.color,
                          mb: 1 
                        }} 
                      />
                    </Box>

                    {/* Tournament Name */}
                    <MKTypography
                      variant="h5"
                      fontWeight="bold"
                      mb={2}
                      sx={{ 
                        textAlign: 'center',
                        color: tournament.error 
                          ? '#999 !important' 
                          : theme.palette.mode === 'dark' ? 'white !important' : '#333333 !important'
                      }}
                    >
                      {tournament.name}
                      {tournament.error && ' (Unavailable)'}
                    </MKTypography>

                    {/* Tournament Details */}
                    <Box sx={{ mb: 3, flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CalendarToday sx={{ fontSize: 16, mr: 1, color: tournament.color }} />
                        <MKTypography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.8) !important' : '#666 !important' }}>
                          {tournament.date}
                        </MKTypography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CalendarToday sx={{ fontSize: 16, mr: 1, color: tournament.color }} />
                        <MKTypography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.8) !important' : '#666 !important' }}>
                          {tournament.time}
                        </MKTypography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocationOn sx={{ fontSize: 16, mr: 1, color: tournament.color }} />
                        <MKTypography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.8) !important' : '#666 !important' }}>
                          {tournament.location}
                        </MKTypography>
                      </Box>
                      
                    </Box>


                    {/* Register Button */}
                    <MKButton
                      variant="contained"
                      fullWidth
                      onClick={() => handleTournamentClick(tournament)}
                      disabled={tournament.error}
                      aria-label={tournament.error ? `Event unavailable: ${tournament.name}` : `View details for ${tournament.name} tournament`}
                      sx={{
                        backgroundColor: tournament.error ? '#ccc' : tournament.color,
                        color: 'white !important',
                        '&:hover': {
                          backgroundColor: tournament.error ? '#ccc' : tournament.color,
                          opacity: 0.9
                        }
                      }}
                    >
                      {tournament.error ? 'Event Unavailable' : 'View Details'}
                    </MKButton>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Code of Conduct */}
        <Box sx={{ mb: 8 }}>
          <MKTypography
            variant="h3"
            fontWeight="bold"
            mb={4}
            sx={{ 
              textAlign: "center",
              color: theme.palette.mode === 'dark' ? 'white !important' : '#333333 !important'
            }}
          >
            Code of Conduct
          </MKTypography>
          
          <Card
            sx={{
              maxWidth: 800,
              mx: "auto",
              background: theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)'
                : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              border: theme.palette.mode === 'dark' 
                ? '1px solid #444'
                : '1px solid #dee2e6',
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark' 
                ? '0 8px 32px rgba(0,0,0,0.3)' 
                : '0 8px 32px rgba(0,0,0,0.1)'
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box component="ul" sx={{ 
                pl: 3, 
                mb: 3,
                '& li': {
                  mb: 1,
                  color: theme.palette.mode === 'dark' ? 'white !important' : '#333333 !important'
                }
              }}>
                <li>No use of derogatory/offensive language</li>
                <li>No discrimination of any kind whatsoever</li>
                <li>No misuse of org/venue property</li>
                <li>No disruptions to the public</li>
                <li>No obscene/Indecent behavior</li>
                <li>No endangering the health/safety of others</li>
                <li>No sexual violence</li>
                <li>No drugs in venue</li>
                <li>No destruction of property/theft</li>
              </Box>
              
              <MKTypography
                variant="body2"
                sx={{ 
                  fontStyle: 'italic',
                  color: theme.palette.mode === 'dark' ? 'white !important' : '#333333 !important',
                  textAlign: 'center'
                }}
              >
                To be determined at the discretion of the TOs. Failure to adhere the code of conduct may result in a ban. The TO's word is final, we prioritize our events running smoothly/on time.
              </MKTypography>
            </CardContent>
          </Card>
        </Box>

      </Container>
    </Box>
  );
}

export default MixCupPage;
